import { asDate } from "@@/shared/date-time/as-date";
import type { TimeRange } from "@@/shared/date-time/time-range";
import { isWithinTimeRange } from "@@/shared/date-time/time-range";
import type { AvailableQuantity } from "@@/shared/units-and-quantities/quantity";
import { sum } from "@@/utilities/sum";
import { differenceInMinutes, isPast, startOfDay, subMinutes } from "date-fns";
import type { BookableSettings } from "../bookable-settings";
import type { Resource } from "../resources/resource";
import type { ResourceGeneric } from "../resources/resource-generic";
import type { ResourceGroup } from "../resources/resource-group";
import { isResourceGroup } from "../resources/resource-group";
import type { ResourceReservationRequestInfo } from "../resources/resource-reservation-info";
import type { BookableSessionId } from "./bookable-session-id";

const isSessionAvailable = (params: {
    timeRange: TimeRange;
    settings: BookableSettings;
    ignoreSessionAvailability: boolean;
}) => {
    const { settings, timeRange, ignoreSessionAvailability } = params;

    const sessionAvailability =
        settings._type === "ACCOMMODATION_SETTINGS"
            ? undefined
            : settings.sessionAvailability;

    const hasSessionAvailability =
        sessionAvailability && !ignoreSessionAvailability;

    //Check if session should not be able to be booked because of "negative prepTime"
    if (hasSessionAvailability && sessionAvailability) {
        const diff =
            sessionAvailability.granularity === "DAY" ||
            sessionAvailability.granularity === "NIGHT"
                ? differenceInMinutes(
                      startOfDay(asDate(timeRange.start)),
                      startOfDay(new Date()),
                  ) /
                  60 /
                  24
                : differenceInMinutes(asDate(timeRange.start), new Date());

        if (Math.floor(diff) > sessionAvailability.duration) {
            return false;
        }
    }

    const pastBefore = settings.allowBookingDuringSession
        ? asDate(timeRange.end)
        : asDate(timeRange.start);

    const legacyPrepTime =
        settings._type === "ACCOMMODATION_SETTINGS"
            ? 0
            : (settings.prepTime ?? 0);

    const advanceNotice =
        settings.advanceNotice !== undefined
            ? settings.advanceNotice
            : legacyPrepTime;
    const hasPassed = isPast(subMinutes(pastBefore, advanceNotice));

    return !hasPassed;
};

const isResourceAvailable = (params: {
    timeRange: TimeRange;
    settings: BookableSettings;
    reservations: ResourceReservationRequestInfo[];
    requestedQuantity: number;
    resource: Exclude<Resource, ResourceGroup>;
    overrideSession: boolean; //If true, will ignore session availability
    sessionId?: BookableSessionId;
}) => {
    // if (isResourceTableGroup(params.resource)) {
    //     return getOverlappingReservations(params).length === 0;
    // }

    const availability = getResourceAvailableQuantity({
        timeRange: params.timeRange,
        settings: params.settings,
        reservations: params.reservations,
        resource: params.resource,
        overrideSession: params.overrideSession,
        sessionId: params.sessionId,
    });
    if (availability.availableQuantity === "INFINITE") return true;
    return availability.availableQuantity - params.requestedQuantity >= 0;
};

const getOverlappingReservations = (params: {
    reservations: ResourceReservationRequestInfo[];
    resource: Resource;
    timeRange: TimeRange;
}) => {
    const reservations = params.reservations.filter(reservation => {
        if (reservation.resourceId !== params.resource._id) return false;

        const validTimeRange =
            isWithinTimeRange(params.timeRange, reservation.timeRange, {
                strict: true,
            }) ||
            isWithinTimeRange(reservation.timeRange, params.timeRange, {
                strict: true,
            }); //Needs to check if there is any overlap
        return validTimeRange;
    });
    return reservations;
};

const getResourceAvailableQuantity = (params: {
    timeRange: TimeRange;
    settings: BookableSettings;
    reservations: ResourceReservationRequestInfo[];
    resource: Exclude<Resource, ResourceGroup>;
    overrideSession: boolean; //If true, will ignore session availability like prepTime
    sessionId?: BookableSessionId;
}): ResourceAvailabilityTimeRange => {
    const { settings, timeRange, resource, overrideSession, sessionId } =
        params;
    if (isResourceGroup(resource))
        throw new Error("RESOURCE_GROUP has no availability");

    const sessionAvailable = isSessionAvailable({
        timeRange,
        settings,
        ignoreSessionAvailability: params.overrideSession, //false,
    });

    if (!sessionAvailable && !overrideSession)
        return {
            resource: resource,
            timeRange: timeRange,
            availableQuantity: 0,
        };

    // Make sure theres enough quantity of resource
    if (resource.quantity === "INFINITE")
        return {
            resource: resource,
            timeRange: timeRange,
            availableQuantity: -1,
        };

    // - Filter to reservations within requested time range

    const reservations = getOverlappingReservations(params);

    //Reserved resources can only be booked on 1 session each
    //time so if there is another session booked this resource cannot be used.
    if (resource._type === "RESOURCE_RESERVED") {
        const existsOthers = reservations.some(r => r.sessionId !== sessionId);
        if (existsOthers) {
            const quantity: ResourceAvailabilityTimeRange = {
                resource: resource,
                timeRange: timeRange,
                availableQuantity: 0,
            };

            return quantity;
        }
    }

    const reservedQuantity = sum(
        reservations,
        reservation => reservation.reservedQuantity,
    );

    const quantity: ResourceAvailabilityTimeRange = {
        resource: resource,
        timeRange: timeRange,
        availableQuantity:
            reservedQuantity > 0 &&
            (resource._type === "RESOURCE_TABLE" ||
                resource._type === "RESOURCE_TABLE_GROUP")
                ? 0
                : resource.quantity - reservedQuantity,
    };

    return quantity;
};

type ResourceAvailabilityTimeRange = {
    resource: Resource | ResourceGeneric;
    timeRange: TimeRange;
    availableQuantity: AvailableQuantity;
};

export {
    getOverlappingReservations,
    getResourceAvailableQuantity,
    isResourceAvailable,
    isSessionAvailable,
};
export type { ResourceAvailabilityTimeRange };
